import {timestampDate} from '@github-ui/dotcom-schema/protobuf'
import type {CacheItem} from '@github-ui/dotcom-schema/types/actions/v1/caches_index'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {TrashIcon} from '@primer/octicons-react'
import {BranchName, IconButton, RelativeTime} from '@primer/react'
import {memo, useRef, useState} from 'react'
import {CachesDeleteDialog} from './CachesDeleteDialog'
import styles from './CachesList.module.css'

const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB']

export function formatCacheSize(bytes: bigint): string {
  if (bytes < 1024n) return `${bytes} ${bytes === 1n ? 'Byte' : 'Bytes'}`
  let unit = 0
  let divisor = 1n
  while (bytes >= divisor * 1024n && unit < units.length - 1) {
    divisor *= 1024n
    unit++
  }
  // Rails uses two significant digits, not two decimal places. Keep int64 arithmetic exact.
  const digits = (bytes / divisor).toString().length
  const scale = 10n ** BigInt(Math.max(0, 2 - digits))
  const rounding = 10n ** BigInt(Math.max(0, digits - 2))
  const rounded = ((bytes * scale + (divisor * rounding) / 2n) / (divisor * rounding)) * rounding
  const whole = rounded / scale
  const fraction = rounded % scale
  return `${whole}${fraction ? `.${fraction}` : ''} ${units[unit]}`
}

export const CachesListItem = memo(function CacheListItem({
  cache,
  canDelete,
  track,
  onDeleted,
}: {
  cache: CacheItem
  canDelete: boolean
  track: (action: string) => void
  onDeleted?: () => Promise<void>
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const deletePath = cache.deletePath
  const created = cache.createdAt ? timestampDate(cache.createdAt).toISOString() : undefined
  const accessed = cache.lastAccessedAt ? timestampDate(cache.lastAccessedAt).toISOString() : undefined
  const refName = cache.branchName || cache.ref
  const branchPath = !cache.ref.startsWith('refs/pull') ? cache.branchPath : undefined

  return (
    <>
      <ListItem
        aria-label={cache.key}
        className={styles.ListItem}
        title={<ListItemTitle value={cache.key} headingClassName={styles.Text} />}
        metadataContainerClassName={styles.MetadataContainer}
        metadata={
          <>
            <ListItemMetadata className={styles.BranchMetadata}>
              {branchPath ? (
                <BranchName href={branchPath} target="_parent" className={styles.Ref} onClick={() => track('branch')}>
                  {refName}
                </BranchName>
              ) : (
                <BranchName as="span" className={styles.Ref}>
                  {refName}
                </BranchName>
              )}
            </ListItemMetadata>
            <ListItemMetadata className={styles.LastUsedMetadata}>
              <span>Last used </span>
              {accessed ? <RelativeTime datetime={accessed} tense="past" /> : <span>at an unknown time</span>}
            </ListItemMetadata>
          </>
        }
        secondaryActions={
          canDelete && deletePath ? (
            <ListItemActionBar label={`actions for cache ${cache.key}`}>
              <IconButton
                ref={triggerRef}
                icon={TrashIcon}
                variant="invisible"
                aria-label={`Delete ${cache.key} on ${refName}`}
                onClick={() => {
                  track('delete_open')
                  setDeleteDialogOpen(true)
                }}
              />
            </ListItemActionBar>
          ) : undefined
        }
      >
        <ListItemMainContent>
          <ListItemDescription>
            <span>{formatCacheSize(cache.sizeInBytes)} cached </span>
            {created ? <RelativeTime datetime={created} tense="past" /> : <span>at an unknown time</span>}
          </ListItemDescription>
        </ListItemMainContent>
      </ListItem>
      {canDelete && deletePath && (
        <CachesDeleteDialog
          cacheKey={cache.key}
          deletePath={deletePath}
          isOpen={deleteDialogOpen}
          returnFocusRef={triggerRef}
          onClose={() => setDeleteDialogOpen(false)}
          track={track}
          onDeleted={onDeleted}
        />
      )}
    </>
  )
})
